import { useState, useCallback, useEffect } from 'react';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { useParams } from 'react-router-dom';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { PageHeader } from '@/shared/ui/PageHeader';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useApiErrorToast } from '@/shared/ui/Toast';

function SimShimmer({ style, c }) {
  return (
    <>
      <style>{`@keyframes simshimmer{0%{background-position:-600px 0}100%{background-position:600px 0}}`}</style>
      <div style={{
        backgroundImage: `linear-gradient(90deg, ${c.bgMuted} 25%, ${c.bgHover} 50%, ${c.bgMuted} 75%)`,
        backgroundSize: '1200px 100%',
        animation: 'simshimmer 1.5s ease-in-out infinite',
        ...style,
      }} />
    </>
  );
}
import { useSimulationConfig, useSimulationPredict, useSimulationOptimal } from '@/features/simulation/hooks/useSimulation';
import { LeverPanel } from '@/features/simulation/ui/LeverPanel';
import { OutcomePanel } from '@/features/simulation/ui/OutcomePanel';

// ── Page ──────────────────────────────────────────────────────────────────────────
export default function DeploymentSimulationPage() {
  const { id: deploymentId } = useParams();
  const c = useThemeColors();
  const isDark = useIsDark();

  const { isMobileOrTablet } = useBreakpoint();
  const toastApiError = useApiErrorToast();
  const [mobileView, setMobileView] = useState('levers'); // 'levers' | 'outcomes'

  const { data: config, isLoading, isError } = useSimulationConfig(deploymentId);
  const predictMutation = useSimulationPredict(deploymentId);
  const optimalMutation  = useSimulationOptimal(deploymentId);

  const [adjustments,      setAdjustments]      = useState({});
  const [activeOutputKeys, setActiveOutputKeys]  = useState(null);
  const [lastResult,       setLastResult]        = useState(null);
  // suggestedSetpoints: what the BE recommends given current context
  const [suggestedSetpoints, setSuggestedSetpoints] = useState(null);

  useEffect(() => {
    if (config?.defaultOutputKeys && activeOutputKeys === null) {
      setActiveOutputKeys(config.defaultOutputKeys);
    }
  }, [config, activeOutputKeys]);

  const runPredict = useCallback(async (adj, keys) => {
    try {
      const result = await predictMutation.mutateAsync({ adjustments: adj, outputKeys: keys });
      setLastResult(result);
    } catch (err) {
      toastApiError(err);
    }
  }, [predictMutation, toastApiError]);

  const handleRunSimulation = useCallback(() => {
    runPredict(adjustments, activeOutputKeys ?? []);
  }, [adjustments, activeOutputKeys, runPredict]);

  // Fetch context-aware suggested setpoints (doesn't apply them — user decides)
  const handleFetchSuggested = useCallback(async () => {
    if (!config) return;
    try {
      const result = await optimalMutation.mutateAsync({ adjustments });
      setSuggestedSetpoints(result?.optimalSetpoints ?? config.optimalSetpoints ?? {});
    } catch {
      setSuggestedSetpoints(config.optimalSetpoints ?? {});
    }
  }, [config, adjustments, optimalMutation]);

  // Apply suggested setpoints to sliders + run predict
  const handleApplySuggested = useCallback((setpoints) => {
    setAdjustments(setpoints);
    runPredict(setpoints, activeOutputKeys ?? config?.defaultOutputKeys ?? []);
  }, [activeOutputKeys, config, runPredict]);

  const handleReset = useCallback(() => {
    setAdjustments({});
    setLastResult(null);
    setSuggestedSetpoints(null);
    if (config?.defaultOutputKeys) setActiveOutputKeys(config.defaultOutputKeys);
  }, [config]);

  const hasModel = !!config?.parameters?.length;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      fontFamily: typography.fontFamily.body,
      background: c.bgPage,
      overflow: 'hidden',
    }}>
      <PageHeader
        title="Simulation"
        description="What-if decision engine — adjust process levers, predict outcomes"
      />

      {isLoading ? (
        <div style={{ flex: 1, display: 'flex', gap: 14, overflow: 'hidden', minHeight: 0 }}>
          {/* Lever panel skeleton */}
          <div style={{
            width: 360, minWidth: 280, flexShrink: 0,
            background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius['2xl'], overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
            <SimShimmer style={{ height: 56, margin: '14px 14px 10px', borderRadius: radius.lg }} c={c} />
            <SimShimmer style={{ height: 32, margin: '0 14px 12px', borderRadius: radius.md }} c={c} />
            <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[70, 55, 70, 60, 55].map((w, i) => <SimShimmer key={i} style={{ height: 36, borderRadius: radius.md, width: `${w}%` }} c={c} />)}
            </div>
          </div>
          {/* Outcome panel skeleton */}
          <div style={{
            flex: 1, background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius['2xl'], overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
            <SimShimmer style={{ height: 44, margin: '14px 16px 12px', borderRadius: radius.lg }} c={c} />
            <div style={{ flex: 1, padding: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, alignContent: 'start' }}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <SimShimmer style={{ height: 13, width: '55%', borderRadius: radius.sm }} c={c} />
                  <SimShimmer style={{ height: 9, width: '35%', borderRadius: radius.sm }} c={c} />
                  <SimShimmer style={{ height: 28, width: '40%', borderRadius: radius.sm }} c={c} />
                  <SimShimmer style={{ height: 100, borderRadius: radius.md }} c={c} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : isError || !hasModel ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            icon="science"
            title="No simulation model configured"
            description="The simulation engine is not yet available for this deployment. Contact your Ripik team to enable it."
          />
        </div>
      ) : (
        <>
        {/* Mobile view toggle */}
        {isMobileOrTablet && (
          <div style={{ display: 'flex', margin: '0 14px 8px', padding: 3, borderRadius: radius.lg, background: c.bgMuted, border: `1px solid ${c.border}`, flexShrink: 0 }}>
            {['levers', 'outcomes'].map((v) => (
              <button
                key={v}
                onClick={() => setMobileView(v)}
                style={{
                  flex: 1, padding: '6px 0', fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, cursor: 'pointer',
                  border: 'none', borderRadius: radius.md,
                  background: mobileView === v ? (isDark ? 'rgba(37,99,235,0.18)' : 'rgba(37,99,235,0.1)') : 'transparent',
                  color: mobileView === v ? semantic.blue : c.textSubtle,
                  fontFamily: typography.fontFamily.body,
                  transition: 'all 0.15s',
                }}
              >
                {v === 'levers' ? 'Process Levers' : 'Outcomes'}
                {v === 'outcomes' && lastResult && (
                  <span style={{ display: 'inline-block', marginLeft: 5, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: semantic.blue, color: '#fff', borderRadius: radius.md, padding: '0 5px', verticalAlign: 'middle' }}>
                    result
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div style={{ flex: 1, display: 'flex', gap: 14, overflow: 'hidden', minHeight: 0, padding: isMobileOrTablet ? '0 0 0 0' : undefined }}>
          <div style={{
            width: isMobileOrTablet ? '100%' : 360,
            minWidth: isMobileOrTablet ? 0 : 280,
            flexShrink: 0,
            overflow: 'hidden',
            display: isMobileOrTablet && mobileView !== 'levers' ? 'none' : 'block',
          }}>
            <LeverPanel
              config={config}
              adjustments={adjustments}
              setAdjustments={setAdjustments}
              suggestedSetpoints={suggestedSetpoints}
              onRunSimulation={handleRunSimulation}
              onFetchSuggested={handleFetchSuggested}
              onApplySuggested={handleApplySuggested}
              onReset={handleReset}
              isPredicting={predictMutation.isPending}
              isFetchingSuggested={optimalMutation.isPending}
              c={c}
              isDark={isDark}
            />
          </div>
          <div style={{
            flex: 1,
            minWidth: isMobileOrTablet ? 0 : 320,
            overflow: 'hidden',
            display: isMobileOrTablet && mobileView !== 'outcomes' ? 'none' : 'block',
          }}>
            <OutcomePanel
              config={config}
              result={lastResult}
              activeOutputKeys={activeOutputKeys ?? config.defaultOutputKeys ?? []}
              setActiveOutputKeys={setActiveOutputKeys}
              isPredicting={predictMutation.isPending}
              c={c}
              isDark={isDark}
            />
          </div>
        </div>
        </>
      )}
    </div>
  );
}
